import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { usePublicMode } from '@/contexts/PublicModeContext';
import { loadGuardianSettings, getActiveMonitorCount } from '@/lib/sessionGuardian';
import { Capacitor } from '@capacitor/core';
import { 
  PrivacySessionManager, 
  enablePrivacySafeLogging, 
  disableLoggingInProduction,
  detectPrivacyMode,
  runPrivacyAudit 
} from '@/lib/privacy';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { isIOS } from '@/lib/platformDetection';

export interface PrivacyScoreBreakdown {
  id: string;
  label: string;
  category: 'encryption' | 'network' | 'device' | 'behavior';
  points: number;
  maxPoints: number;
  enabled: boolean;
  tip?: string;
}

interface PrivacyState {
  isLocked: boolean;
  autoLockEnabled: boolean;
  autoLockTimeout: number;
  screenshotProtection: boolean;
  clipboardProtection: boolean;
  voiceCallsDisabled: boolean;
  deliveryReceipts: boolean;
  ghostMode: boolean;
  hideMessagePreviews: boolean;
  privacyMode: {
    isTor: boolean;
    isPrivateBrowsing: boolean;
    hasReducedFingerprint: boolean;
  };
  privacyScore: number;
  privacyBreakdown: PrivacyScoreBreakdown[];
}

interface PrivacyContextType extends PrivacyState {
  setAutoLockTimeout: (minutes: number) => void;
  setAutoLockEnabled: (enabled: boolean) => void;
  setScreenshotProtection: (enabled: boolean) => void;
  setClipboardProtection: (enabled: boolean) => void;
  setVoiceCallsDisabled: (disabled: boolean) => void;
  setDeliveryReceipts: (enabled: boolean) => void;
  setGhostMode: (enabled: boolean) => void;
  setHideMessagePreviews: (enabled: boolean) => void;
  lockNow: () => void;
  getInactiveTime: () => number;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

const PRIVACY_SETTINGS_KEY = 'vaultmsg_privacy_settings';

interface PrivacySettings {
  autoLockEnabled: boolean;
  autoLockTimeout: number;
  screenshotProtection: boolean;
  clipboardProtection: boolean;
  voiceCallsDisabled: boolean;
  deliveryReceipts: boolean;
  ghostMode: boolean;
  hideMessagePreviews: boolean;
}

function loadPrivacySettings(): PrivacySettings {
  try {
    const stored = localStorage.getItem(PRIVACY_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore
  }
  return {
    autoLockEnabled: true,
    autoLockTimeout: 5,
    screenshotProtection: true,
    clipboardProtection: true,
    voiceCallsDisabled: false,
    deliveryReceipts: true,
    ghostMode: false,
    hideMessagePreviews: false,
  };
}

function savePrivacySettings(settings: PrivacySettings): void {
  try { localStorage.setItem(PRIVACY_SETTINGS_KEY, JSON.stringify(settings)); } catch {}
}

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const { logout, isAuthenticated } = useAuth();
  const { isPublicMode } = usePublicMode();
  
  const [settings, setSettings] = useState<PrivacySettings>(loadPrivacySettings);
  const [isLocked, setIsLocked] = useState(false);
  const [privacyMode, setPrivacyMode] = useState({
    isTor: false,
    isPrivateBrowsing: false,
    hasReducedFingerprint: false,
  });
  const [privacyScore, setPrivacyScore] = useState(0);
  const [privacyBreakdown, setPrivacyBreakdown] = useState<PrivacyScoreBreakdown[]>([]);
  const [sessionManager, setSessionManager] = useState<PrivacySessionManager | null>(null);

  // Initialize privacy protections
  useEffect(() => {
    // Enable privacy-safe logging
    enablePrivacySafeLogging();
    
    // Disable logging in production
    disableLoggingInProduction();
    
    // Initial privacy mode detection
    setPrivacyMode(detectPrivacyMode());
    
    // Prevent right-click context menu on sensitive elements
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-sensitive]') || target.closest('[data-no-context]')) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    
    // Prevent drag on sensitive elements
    const handleDrag = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-sensitive]')) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('dragstart', handleDrag);
    
    // Blur sensitive data when window loses focus
    const handleBlur = () => {
      document.querySelectorAll('[data-sensitive]').forEach(el => {
        el.classList.add('blur-sm');
      });
    };
    
    const handleFocus = () => {
      document.querySelectorAll('[data-sensitive]').forEach(el => {
        el.classList.remove('blur-sm');
      });
    };
    
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDrag);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Auto-lock session manager
  useEffect(() => {
    if (!isAuthenticated || !settings.autoLockEnabled) {
      sessionManager?.destroy();
      setSessionManager(null);
      return;
    }

    const handleTimeout = () => {
      logout();
      setIsLocked(true);
      // Silent — locked due to inactivity
    };

    const manager = new PrivacySessionManager(
      settings.autoLockTimeout * 60 * 1000,
      handleTimeout
    );
    
    setSessionManager(manager);
    
    return () => {
      manager.destroy();
    };
  }, [isAuthenticated, settings.autoLockEnabled, settings.autoLockTimeout, logout]);

  // Reset lock state when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setIsLocked(false);
    }
  }, [isAuthenticated]);

  // Reactive privacy score — recalculates whenever any setting or mode changes
  useEffect(() => {
    const breakdown: PrivacyScoreBreakdown[] = [
      // ── Encryption (250 pts) ──
      { id: 'e2ee', label: 'End-to-end encryption', category: 'encryption', points: 120, maxPoints: 120, enabled: true, tip: 'Always active — messages are encrypted with X25519 + XSalsa20-Poly1305' },
      { id: 'signatures', label: 'Message signing', category: 'encryption', points: 80, maxPoints: 80, enabled: true, tip: 'Ed25519 signatures verify message authenticity' },
      { id: 'key_exchange', label: 'Secure key exchange', category: 'encryption', points: 50, maxPoints: 50, enabled: true, tip: 'Diffie-Hellman key exchange ensures forward secrecy per session' },

      // ── Device (250 pts) ──
      { id: 'autolock', label: 'Auto-lock', category: 'device', points: settings.autoLockEnabled ? 60 : 0, maxPoints: 60, enabled: settings.autoLockEnabled, tip: settings.autoLockEnabled ? `Locks after ${settings.autoLockTimeout} min of inactivity` : 'Enable to auto-lock when idle' },
      { id: 'autolock_time', label: 'Lock timeout ≤ 5 min', category: 'device', points: settings.autoLockEnabled && settings.autoLockTimeout <= 5 ? 40 : 0, maxPoints: 40, enabled: settings.autoLockEnabled && settings.autoLockTimeout <= 5, tip: 'Short timeouts reduce window of exposure' },
      { id: 'autolock_strict', label: 'Lock timeout ≤ 2 min', category: 'device', points: settings.autoLockEnabled && settings.autoLockTimeout <= 2 ? 20 : 0, maxPoints: 20, enabled: settings.autoLockEnabled && settings.autoLockTimeout <= 2, tip: 'Ultra-short timeout for maximum device security' },
      { id: 'screenshot', label: 'Screenshot protection', category: 'device', points: settings.screenshotProtection ? 70 : 0, maxPoints: 70, enabled: settings.screenshotProtection, tip: 'Blocks screen capture on supported devices' },
      { id: 'clipboard', label: 'Clipboard protection', category: 'device', points: settings.clipboardProtection ? 60 : 0, maxPoints: 60, enabled: settings.clipboardProtection, tip: 'Prevents copying sensitive content to clipboard' },

      // ── Network (200 pts) ──
      { id: 'tor', label: 'Tor / onion routing', category: 'network', points: privacyMode.isTor ? 100 : 0, maxPoints: 100, enabled: privacyMode.isTor, tip: privacyMode.isTor ? 'Traffic routed through Tor' : 'Use Tor Browser for anonymous routing' },
      { id: 'private_browsing', label: 'Private browsing', category: 'network', points: (privacyMode.isPrivateBrowsing || Capacitor.isNativePlatform()) ? 50 : 0, maxPoints: 50, enabled: privacyMode.isPrivateBrowsing || Capacitor.isNativePlatform(), tip: Capacitor.isNativePlatform() ? 'Native app — no browser history or cookies stored' : 'Incognito mode prevents local history storage' },
      { id: 'fingerprint', label: 'Reduced fingerprint', category: 'network', points: privacyMode.hasReducedFingerprint ? 50 : 0, maxPoints: 50, enabled: privacyMode.hasReducedFingerprint, tip: 'Browser fingerprint reduction active' },

      // ── Behavior (150 pts) ──
      { id: 'no_read_receipts', label: 'No read receipts', category: 'behavior', points: 40, maxPoints: 40, enabled: true, tip: 'Read receipts are permanently disabled to prevent behavioral metadata leaks' },
      { id: 'delivery_control', label: 'Delivery receipts off', category: 'behavior', points: !settings.deliveryReceipts ? 30 : 0, maxPoints: 30, enabled: !settings.deliveryReceipts, tip: settings.deliveryReceipts ? 'Disabling delivery receipts hides your online status' : 'Delivery receipts are off — maximum stealth' },
      { id: 'voice_disabled', label: 'Voice calls disabled', category: 'behavior', points: settings.voiceCallsDisabled ? 25 : 0, maxPoints: 25, enabled: settings.voiceCallsDisabled, tip: settings.voiceCallsDisabled ? 'Voice calls disabled — prevents WebRTC IP leaks' : 'Voice calls can leak IP via WebRTC' },
      { id: 'vault_pin', label: 'Vault PIN active', category: 'behavior', points: !!localStorage.getItem('aphenis_vault_pin') ? 25 : 0, maxPoints: 25, enabled: !!localStorage.getItem('aphenis_vault_pin'), tip: 'Your saved messages vault is protected with a PIN' },
      { id: 'hide_previews', label: 'Hidden message previews', category: 'behavior', points: settings.hideMessagePreviews ? 30 : 0, maxPoints: 30, enabled: settings.hideMessagePreviews, tip: settings.hideMessagePreviews ? 'Message previews are hidden in chat list — prevents shoulder surfing' : 'Enable to hide message content in the chat list' },
    ];

    // ── Session Guardian (150 pts) ──
    const guardianSettings = loadGuardianSettings();
    const guardianActive = guardianSettings.enabled;
    const guardianMonitors = getActiveMonitorCount(guardianSettings);
    
    breakdown.push(
      { id: 'guardian_enabled', label: 'Session Guardian', category: 'device', points: guardianActive ? 40 : 0, maxPoints: 40, enabled: guardianActive, tip: guardianActive ? 'Real-time session protection is active' : 'Enable Session Guardian for real-time threat monitoring' },
      { id: 'guardian_monitors', label: 'Guardian monitors', category: 'device', points: guardianActive ? Math.min(guardianMonitors * 7, 70) : 0, maxPoints: 70, enabled: guardianActive && guardianMonitors >= 5, tip: guardianActive ? `${guardianMonitors}/10 monitors active — more = higher score` : 'Enable monitors in Session Guardian settings' },
      { id: 'guardian_lockdown', label: 'Lockdown mode', category: 'device', points: guardianActive && guardianSettings.lockdownMode ? 40 : 0, maxPoints: 40, enabled: guardianActive && guardianSettings.lockdownMode, tip: guardianSettings.lockdownMode ? 'Maximum protection — any threat locks session' : 'Enable lockdown mode for maximum paranoia' },
    );

    const total = breakdown.reduce((s, b) => s + b.points, 0);
    setPrivacyScore(total);
    setPrivacyBreakdown(breakdown);
  }, [settings, privacyMode]);

  const updateSettings = useCallback((updates: Partial<PrivacySettings>) => {
    setSettings(prev => {
      const newSettings = { ...prev, ...updates };
      savePrivacySettings(newSettings);
      return newSettings;
    });
  }, []);

  const lockNow = useCallback(() => {
    logout();
    setIsLocked(true);
    // Silent — locked
  }, [logout]);

  const getInactiveTime = useCallback(() => {
    return sessionManager?.getInactiveTime() ?? 0;
  }, [sessionManager]);

  // In Public Mode, override all privacy settings to permissive defaults
  const effectiveValues = useMemo(() => {
    if (isPublicMode) {
      return {
        autoLockEnabled: false,
        autoLockTimeout: settings.autoLockTimeout,
        screenshotProtection: false,
        clipboardProtection: false,
        voiceCallsDisabled: false,
        deliveryReceipts: true,
        ghostMode: false,
        hideMessagePreviews: false,
      };
    }
    // On iOS, screenshot protection doesn't work (no FLAG_SECURE, no WebKit API).
    // Force it off to prevent the JS visibility-change overlay from blocking the app.
    const iosForceOff = isIOS();
    return {
      autoLockEnabled: settings.autoLockEnabled,
      autoLockTimeout: settings.autoLockTimeout,
      screenshotProtection: iosForceOff ? false : settings.screenshotProtection,
      clipboardProtection: settings.clipboardProtection,
      voiceCallsDisabled: settings.voiceCallsDisabled,
      deliveryReceipts: settings.deliveryReceipts,
      ghostMode: settings.ghostMode,
      hideMessagePreviews: settings.hideMessagePreviews,
    };
  }, [isPublicMode, settings]);

  return (
    <PrivacyContext.Provider
      value={{
        isLocked,
        autoLockEnabled: effectiveValues.autoLockEnabled,
        autoLockTimeout: effectiveValues.autoLockTimeout,
        screenshotProtection: effectiveValues.screenshotProtection,
        clipboardProtection: effectiveValues.clipboardProtection,
        voiceCallsDisabled: effectiveValues.voiceCallsDisabled,
        deliveryReceipts: effectiveValues.deliveryReceipts,
        ghostMode: effectiveValues.ghostMode,
        hideMessagePreviews: effectiveValues.hideMessagePreviews,
        privacyMode,
        privacyScore,
        privacyBreakdown,
        setAutoLockTimeout: (minutes) => updateSettings({ autoLockTimeout: minutes }),
        setAutoLockEnabled: (enabled) => updateSettings({ autoLockEnabled: enabled }),
        setScreenshotProtection: (enabled) => updateSettings({ screenshotProtection: enabled }),
        setClipboardProtection: (enabled) => updateSettings({ clipboardProtection: enabled }),
        setVoiceCallsDisabled: (disabled) => updateSettings({ voiceCallsDisabled: disabled }),
        setDeliveryReceipts: (enabled) => updateSettings({ deliveryReceipts: enabled }),
        setGhostMode: (enabled) => updateSettings({ ghostMode: enabled }),
        setHideMessagePreviews: (enabled) => updateSettings({ hideMessagePreviews: enabled }),
        lockNow,
        getInactiveTime,
      }}
    >
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacy() {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
}
